/**
 * Utility functions for creating truck marker icons
 * Extracted from LiveMap for reuse across components
 */

import { escapeHtml, sanitizeNumber } from './sanitize';

// Truck type for different cargo configurations
export type TruckType = 'TruckWithContainer' | 'TruckWithChassis' | 'TruckEmpty' | 'Mule';

export interface TruckMarkerOptions {
  speed?: number;
  heading?: number;
  hasDispatch?: boolean;
  isCompleted?: boolean;
  remainingDispatches?: number;
  hasOriginArrivalTime?: boolean;
  truckType?: TruckType;
  sizeMultiplier?: number;
  unitNumber?: string;
  isSelected?: boolean;
  isDarkMode?: boolean;
  opacity?: number;
  isOOS?: boolean;
}

/**
 * Custom truck icon generator with rotation support
 * Returns an HTMLElement for use with Google Maps Marker
 *
 * Status-based colors:
 * - Selected (#000000 light mode / #FFFFFF dark mode): Selected truck (overrides all other colors)
 * - Red (#EF4444): Out Of Service (overrides all other colors except selected)
 * - Blue (#3B82F6): In Progress (has dispatch, not completed)
 * - Orange (#F59E0B): Not Started (has remaining dispatches but no origin arrival time)
 * - Grey (#6B7280): Dispatched (completed with remaining > 0 and has origin arrival time)
 * - Green (#10B981): Available (no dispatch OR completed with 0 remaining)
 */
export const createTruckMarkerIcon = (options: TruckMarkerOptions = {}): HTMLElement => {
  const {
    speed = 0,
    heading,
    hasDispatch = false,
    isCompleted = false,
    remainingDispatches = 0,
    hasOriginArrivalTime = false,
    truckType = 'TruckWithContainer',
    sizeMultiplier = 1,
    unitNumber = '',
    isSelected = false,
    isDarkMode = false,
    opacity = 1,
    isOOS = false,
  } = options;

  // Helper function to convert hex to rgba with opacity
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Determine color based on status
  let baseColor: string;
  let strokeColor: string;

  if (isOOS) {
    baseColor = '#EF4444';  // Red - Out Of Service (overrides selected state)
    strokeColor = isSelected ? 'none' : hexToRgba('#000000', opacity);
  } else if (isSelected) {
    baseColor = isDarkMode ? '#FFFFFF' : '#000000';  // White in dark mode, Black in light mode
    strokeColor = 'none';  // Hide stroke when selected
  } else {
    strokeColor = hexToRgba('#000000', opacity);
    if (hasDispatch && !isCompleted) {
      baseColor = '#3B82F6';  // Blue - In Progress
    } else if (remainingDispatches > 0 && !hasOriginArrivalTime) {
      baseColor = '#F59E0B';  // Orange - Not Started
    } else if (hasDispatch && isCompleted && remainingDispatches > 0) {
      baseColor = '#6B7280';  // Grey - Dispatched
    } else {
      baseColor = '#10B981';  // Green - Available
    }
  }

  // Apply opacity to the color
  const color = hexToRgba(baseColor, opacity);

  // Set size for truck icons
  const baseSize = truckType === 'TruckEmpty' ? 32 : truckType === 'Mule' ? 32 : 60;
  const size = Math.round(baseSize * sizeMultiplier);

  const container = document.createElement('div');
  container.style.width = `${size}px`;
  container.style.height = `${size}px`;
  container.style.transform = 'translate(-50%, -50%)';
  container.style.position = 'absolute';

  // Calculate rotation based on heading
  let rotation: number;
  let scaleY = 1;

  if (speed <= 5) {
    // Stopped truck - face right
    rotation = 0;
    scaleY = 1;
  } else {
    // Moving truck - use heading
    const actualHeading = heading !== undefined && heading !== null ? heading : 90;
    rotation = actualHeading - 90;

    // Normalize rotation to keep truck right-side up
    if (rotation > 90) {
      scaleY = -1;
      rotation = rotation - 180;
    } else if (rotation < -90) {
      scaleY = -1;
      rotation = rotation + 180;
    } else {
      scaleY = 1;
    }
  }

  // Use different truck icons based on truck type
  switch (truckType) {
    case 'TruckWithContainer':
      if (scaleY === -1) {
        // Left-facing version (pre-flipped)
        container.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="${size}" height="${size}">
            <g transform="translate(31, 9) rotate(${rotation}) translate(-31, -9)">
              <g transform="translate(-1.4, -22.39)">
                <g transform="matrix(-1,0,0,1,64.0127,0.263741)">
                  <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="matrix(-1,0,0,1,64.0337,0.296808)">
                  <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="translate(41.1, 24.9078)">
                  <text x="0px" y="7.756px" style="font-family:var(--app-font-family);font-size:8px;fill:white;text-anchor:middle;">${escapeHtml(unitNumber)}</text>
                </g>
              </g>
            </g>
          </svg>
        `;
      } else {
        // Right-facing version
        container.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="${size}" height="${size}">
            <g transform="translate(31, 9) rotate(${rotation}) translate(-31, -9)">
              <g transform="translate(-1.4, -22.39)">
                <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
                  fill="${color}"
                  stroke="${strokeColor}"
                  strokeWidth="0.4"/>
                <g transform="translate(0.033063, 0.0104117)">
                  <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="translate(1.68205, 24.8414)">
                  <text x="22px" y="7.756px" style="font-family:var(--app-font-family);font-size:8px;fill:white;text-anchor:middle;">${escapeHtml(unitNumber)}</text>
                </g>
              </g>
            </g>
          </svg>
        `;
      }
      break;

    case 'TruckWithChassis':
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 15" width="${size}" height="${size}">
          <g transform="translate(31, 7.5) rotate(${rotation}) translate(-31, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 1.472,34.325 1.472,34.325C1.472,34.655 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM56.16,31.37C56.16,31.37 56.16,31.39 56.17,31.4L53.6,31.4L53.6,30.6L55.77,30.6C55.92,30.85 56.05,31.11 56.16,31.37ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-1.47155, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;

    case 'TruckEmpty':
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 15" width="${size}" height="${size}">
          <g transform="translate(14.5, 7.5) rotate(${rotation}) translate(-14.5, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-34.3558, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;

    case 'Mule':
      // Mule vehicle - currently using TruckEmpty SVG as placeholder
      // TODO: Replace the SVG below with a custom mule/yard truck icon
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 15" width="${size}" height="${size}">
          <g transform="translate(14.5, 7.5) rotate(${rotation}) translate(-14.5, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-34.3558, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;
  }

  return container;
};

/**
 * Get status color for a driver
 * Useful for displaying color indicators in UI
 */
export const getDriverStatusColor = (
  hasDispatch: boolean,
  isCompleted: boolean,
  remainingDispatches: number = 0,
  hasOriginArrivalTime: boolean = false,
  isOOS: boolean = false
): string => {
  if (isOOS) return '#EF4444';  // Red - Out Of Service
  if (hasDispatch && !isCompleted) return '#3B82F6';  // Blue - In Progress
  if (remainingDispatches > 0 && !hasOriginArrivalTime) return '#F59E0B';  // Orange - Not Started
  if (hasDispatch && isCompleted && remainingDispatches > 0) return '#6B7280';  // Grey - Dispatched
  return '#10B981';  // Green - Available
};

/**
 * Get status label for a driver
 */
export const getDriverStatusLabel = (
  hasDispatch: boolean,
  isCompleted: boolean,
  remainingDispatches: number = 0,
  hasOriginArrivalTime: boolean = false,
  isOOS: boolean = false
): string => {
  if (isOOS) return 'Out of Service';
  if (hasDispatch && !isCompleted) return 'In Progress';
  if (remainingDispatches > 0 && !hasOriginArrivalTime) return 'Not Started';
  if (hasDispatch && isCompleted && remainingDispatches > 0) return 'Dispatched';
  return 'Available';
};
